import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLeaderboard, usePersonalLeaderboard, useSeasons } from '../api/hooks';
import { ErrorView, EmptyView } from '../components/StateViews';
import { CosmeticAvatar } from '../components/cosmetics/CosmeticAvatar';
import { NickFx } from '../components/cosmetics/NickFx';
import { RowFxLayer, rowFxClass } from '../components/cosmetics/RowFx';
import { HexBadge } from '../components/rating/HexBadge';
import { haptics } from '../telegram';
import { track } from '../analytics';
import { seasonChapter } from '../format';
import { TrophyIcon } from '../components/icons';
import type { LeaderboardRow, PersonalLeaderboardRow } from '../api/types';

const SEASONS_PER_CHAPTER = 10;

/** Последний выбранный таб «Сезон | Личный рейтинг» переживает перезапуск. */
const TAB_KEY = 'straddle_rating_tab';
type RatingTab = 'season' | 'personal';
function loadTab(): RatingTab {
  try {
    return localStorage.getItem(TAB_KEY) === 'personal' ? 'personal' : 'season';
  } catch {
    return 'season';
  }
}

function Row({ r, label }: { r: LeaderboardRow; label?: string }) {
  // A fresh season ranks everyone #1 with 0 points — no crowns until scored.
  const leader = r.rank === 1 && r.points > 0;
  return (
    <Link
      className={`lb-row ${r.isMe ? 'me' : ''} ${leader ? 'first' : ''}${rowFxClass(r.cosmetics?.row)}`}
      to={`/players/${r.playerId}`}
      onClick={() => track('player_open', { playerId: r.playerId, from: 'leaderboard' })}
    >
      <RowFxLayer cosmetic={r.cosmetics?.row} height={44} />
      <div className="lb-rank">{r.rank}</div>
      <CosmeticAvatar name={r.name} src={r.avatarUrl} size={28} frame={r.cosmetics?.frame} />
      <div className="lb-name">
        <NickFx cosmetic={r.cosmetics?.nick}>{label ?? r.name}</NickFx>
        {leader ? ' ♛' : ''}
      </div>
      <div className="lb-points">{r.points}</div>
    </Link>
  );
}

const TrendIcon = ({ trend }: { trend: 'up' | 'down' | 'flat' }) =>
  trend === 'up' ? (
    <span className="plb-trend up">▲</span>
  ) : trend === 'down' ? (
    <span className="plb-trend down">▼</span>
  ) : (
    <span className="plb-trend flat">—</span>
  );

const ClockMini = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 3h10M7 21h10M8 3v2.5c0 2.2 4 3.8 4 6.5s-4 4.3-4 6.5V21M16 3v2.5c0 2.2-4 3.8-4 6.5s4 4.3 4 6.5V21" />
  </svg>
);

function PersonalRow({ r, label }: { r: PersonalLeaderboardRow; label?: string }) {
  return (
    <Link
      className={`plb-row ${r.isMe ? 'me' : ''}${rowFxClass(r.cosmetics?.row)}`}
      to={`/players/${r.playerId}`}
      onClick={() => track('player_open', { playerId: r.playerId, from: 'personal_leaderboard' })}
    >
      <RowFxLayer cosmetic={r.cosmetics?.row} height={52} />
      <div className={`plb-rank ${r.rank <= 3 ? 'plb-rank--top' : ''}`}>{r.rank}</div>
      <CosmeticAvatar name={r.name} src={r.avatarUrl} size={28} frame={r.cosmetics?.frame} />
      <div className="plb-name">
        <NickFx cosmetic={r.cosmetics?.nick}>{label ?? r.name}</NickFx>
      </div>
      <HexBadge level={r.level} size={17} />
      <div className={`plb-value mono ${r.decayActive ? 'plb-value--decay' : ''}`}>
        {r.decayActive && (
          <span className="plb-clock" title="Неактивен">
            <ClockMini />
          </span>
        )}
        {r.value}
      </div>
      <TrendIcon trend={r.trend} />
    </Link>
  );
}

/** RC1: сегмент-переключатель «Сезон | Личный рейтинг» на экране «Рейтинг». */
export function LeaderboardScreen() {
  const [tab, setTab] = useState<RatingTab>(loadTab);
  const pickTab = (t: RatingTab) => {
    haptics.tap();
    track('rating_tab', { tab: t });
    setTab(t);
    try {
      localStorage.setItem(TAB_KEY, t);
    } catch {
      /* приватный режим — просто не запомним */
    }
  };

  return (
    <div>
      <div className="screen-title-row">
        <div className="screen-title">Таблица лидеров</div>
        <Link
          className="iconbtn iconbtn--gold"
          to="/hall-of-fame"
          aria-label="Зал славы"
          onClick={() => {
            haptics.tap();
            track('hall_of_fame_open', { from: 'leaderboard' });
          }}
        >
          <TrophyIcon />
        </Link>
      </div>

      <div className="segmented segmented--rating" role="radiogroup" aria-label="Тип рейтинга">
        <button className={tab === 'season' ? 'active' : ''} onClick={() => pickTab('season')}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path d="M7.9 3.6h8.2v5.2a4.1 4.1 0 0 1-8.2 0V3.6zM7.9 5.3H5.3v1.9a3.3 3.3 0 0 0 3.3 3.3M16.1 5.3h2.6v1.9a3.3 3.3 0 0 1-3.3 3.3M12 12.9v3.4M8.6 20.4h6.8l-.8-4.1H9.4l-.8 4.1z" />
          </svg>
          Сезон
        </button>
        <button
          className={tab === 'personal' ? 'active active--skill' : ''}
          onClick={() => pickTab('personal')}
        >
          <svg width="12" height="13" viewBox="0 0 24 26" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinejoin="round">
            <path d="M12 1.3l10.2 5.9v11.6L12 24.7 1.8 18.8V7.2z" />
          </svg>
          Личный рейтинг
        </button>
      </div>

      {tab === 'season' ? <SeasonBoard /> : <PersonalBoard />}
    </div>
  );
}

/** R1 «Главы + сезоны»: chapter stepper ‹ › + up to 10 season tiles per chapter
 *  (numbering resets each chapter; the absolute id stays internal). */
function SeasonBoard() {
  const seasons = useSeasons();
  const [seasonId, setSeasonId] = useState<number | null>(null);
  const [chapter, setChapter] = useState<number | null>(null);

  // Seasons that actually have games, by absolute id.
  const played = useMemo(() => new Set((seasons.data ?? []).map((s) => s.id)), [seasons.data]);
  const maxChapter = useMemo(
    () => (seasons.data?.length ? Math.max(...seasons.data.map((s) => s.chapter)) : 1),
    [seasons.data],
  );

  useEffect(() => {
    if (seasonId === null && seasons.data && seasons.data.length > 0) {
      const active = seasons.data.find((s) => s.isActive) ?? seasons.data[0];
      setSeasonId(active.id);
      setChapter(active.chapter);
    }
  }, [seasons.data, seasonId]);

  const lb = useLeaderboard(seasonId);

  if (seasons.isError) return <ErrorView message={(seasons.error as Error)?.message} />;
  if (chapter === null || seasonId === null) return <LeaderboardSkeleton />;

  const seasonInChapter = seasonId !== null ? ((seasonId - 1) % SEASONS_PER_CHAPTER) + 1 : 1;
  const selectedInThisChapter = seasonChapter(seasonId) === chapter;

  const stepChapter = (delta: number) => {
    const next = chapter + delta;
    if (next < 1 || next > maxChapter) return;
    haptics.tap();
    track('season_switch', { chapter: next });
    setChapter(next);
    // Jump selection to the latest played season of that chapter.
    const inChapter = (seasons.data ?? []).filter((s) => s.chapter === next);
    if (inChapter.length) setSeasonId(Math.max(...inChapter.map((s) => s.id)));
  };

  const pickSeason = (k: number) => {
    const abs = (chapter - 1) * SEASONS_PER_CHAPTER + k;
    if (!played.has(abs)) return;
    haptics.tap();
    track('season_switch', { seasonId: abs, chapter });
    setSeasonId(abs);
  };

  return (
    <div>
      <div className="chapter-stepper">
        <button
          className="step"
          onClick={() => stepChapter(-1)}
          disabled={chapter <= 1}
          aria-label="Предыдущая глава"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4}>
            <path d="M15 5l-7 7 7 7" />
          </svg>
        </button>
        <div className="mid">
          <div className="ch">Глава {chapter}</div>
          <div className="sub">
            {selectedInThisChapter ? `СЕЗОН ${seasonInChapter} ИЗ ${SEASONS_PER_CHAPTER}` : `СЕЗОНОВ: ${SEASONS_PER_CHAPTER}`}
          </div>
        </div>
        <button
          className="step step--fwd"
          onClick={() => stepChapter(1)}
          disabled={chapter >= maxChapter}
          aria-label="Следующая глава"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4}>
            <path d="M9 6l6 6-6 6" />
          </svg>
        </button>
      </div>

      <div className="season-tiles">
        {Array.from({ length: SEASONS_PER_CHAPTER }, (_, i) => {
          const k = i + 1;
          const abs = (chapter - 1) * SEASONS_PER_CHAPTER + k;
          const isCurrent = abs === seasonId;
          const isPlayed = played.has(abs);
          return (
            <button
              key={k}
              className={`tile ${isCurrent ? 'tile--current' : isPlayed ? '' : 'tile--future'}`}
              disabled={!isPlayed}
              onClick={() => pickSeason(k)}
            >
              {k}
            </button>
          );
        })}
      </div>

      <div className="lb-head">
        <span>Место · Игрок</span>
        <span>Очки</span>
      </div>

      {lb.isLoading ? (
        <LeaderboardSkeleton rowsOnly />
      ) : lb.isError ? (
        <ErrorView message={(lb.error as Error)?.message} />
      ) : !lb.data || lb.data.rows.length === 0 ? (
        <EmptyView message="В этом сезоне ещё нет результатов" />
      ) : (
        <>
          <div className="lb-list">
            {lb.data.rows.map((r) => (
              <Row key={r.playerId} r={r} />
            ))}
          </div>
          {lb.data.me && (
            // key: Telegram-вебвью (WebKit) перерисовывает sticky-слой лениво —
            // без пересоздания узла место обновлялось только при прокрутке.
            <div className="me-sticky you-row" key={`me-${seasonId}`}>
              <Row r={lb.data.me} label={`Вы · ${lb.data.me.name}`} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** RC1: карьерный лидерборд личного рейтинга (игроки с 5+ играми). */
function PersonalBoard() {
  const lb = usePersonalLeaderboard();

  if (lb.isLoading) return <LeaderboardSkeleton rowsOnly />;
  if (lb.isError) return <ErrorView message={(lb.error as Error)?.message} />;
  if (!lb.data) return null;

  const { rows, me, meGamesPlayed, calibrationGames } = lb.data;

  return (
    <div>
      <div className="plb-caption">
        Вся карьера · {rows.length} игроков с {calibrationGames}+ играми
      </div>

      <div className="plb-head mono">
        <span className="c-rank">#</span>
        <span className="c-player">игрок</span>
        <span className="c-lvl">ур.</span>
        <span className="c-val">рейт.</span>
        <span className="c-trend"></span>
      </div>

      {rows.length === 0 ? (
        <EmptyView message="Пока никто не сыграл 5 игр — рейтинг впереди" />
      ) : (
        <div className="lb-list">
          {rows.map((r) => (
            <PersonalRow key={r.playerId} r={r} />
          ))}
        </div>
      )}

      {me ? (
        <div className="me-sticky you-row" key="me-personal">
          <PersonalRow r={me} label={`Вы · ${me.name}`} />
        </div>
      ) : (
        <div className="me-sticky plb-newbie" key="me-newbie">
          <div className="plb-newbie-segs">
            {Array.from({ length: calibrationGames }, (_, i) => (
              <i key={i} className={i < meGamesPlayed ? 'on' : ''} />
            ))}
          </div>
          <div className="plb-newbie-text">
            Сыграй ещё {Math.max(0, calibrationGames - meGamesPlayed)} игры, чтобы попасть в
            рейтинг · {meGamesPlayed}/{calibrationGames}
          </div>
        </div>
      )}
    </div>
  );
}

/** Shimmer placeholders (handoff: skeleton rows while loading). */
function LeaderboardSkeleton({ rowsOnly = false }: { rowsOnly?: boolean }) {
  return (
    <div>
      {!rowsOnly && (
        <>
          <div className="skeleton" style={{ height: 34, borderRadius: 10, margin: '4px 0 13px' }} />
          <div className="skeleton" style={{ height: 62, borderRadius: 9, marginBottom: 12 }} />
        </>
      )}
      {Array.from({ length: 8 }, (_, i) => (
        <div key={i} className="skeleton" style={{ height: 34, borderRadius: 10, marginBottom: 8 }} />
      ))}
    </div>
  );
}
